import orderBy from "lodash.orderby";
import {
  SeriesDatum,
  Series,
  SummaryDatum,
  Portfolio,
  Zone,
  RankDir,
  ViewBy,
} from "./types";
import { IndexByString } from "../../../redux/modules/insights/utils";

/**
 *
 * @param zones time series points for individual site
 *
 * Used to reduce time series line chart data down to averages
 * for stacked bar chart summary data
 */
export function calcSiteAvgComfort(data: SeriesDatum[]): SummaryDatum {
  const avgs = data.reduce(
    (acc, d) => {
      return {
        pctHot: acc.pctHot + d.totalPctHot,
        pctCold: acc.pctCold + d.totalPctCold,
        pctComfort: acc.pctComfort + d.totalPctComfort,
      };
    },
    {
      pctHot: 0,
      pctCold: 0,
      pctComfort: 0,
    }
  );

  return {
    pctHot: avgs.pctHot / data.length,
    pctCold: avgs.pctCold / data.length,
    pctComfort: avgs.pctComfort / data.length,
  };
}

type Datum = Series & {
  name: string;
};

/**
 * Combines multiple site series into summary averages
 */
export function calcSitePortfolio(data: IndexByString<Datum>) {
  const portfolio = Object.entries(data).reduce(
    (acc: Portfolio, [key, site]) => {
      // * Site series data
      const series = {
        current: site.current.series,
        compare: site.compare ? site.compare.series : undefined,
      };

      // * Site summary data
      const summary = {
        current: {
          from: site.current.from,
          to: site.current.to,
          ...calcSiteAvgComfort(site.current.series),
        },
        compare: site.compare
          ? {
              from: site.compare.from,
              to: site.compare.to,
              ...calcSiteAvgComfort(site.compare.series),
            }
          : undefined,
      };

      const composed = {
        name: site.name,
        series,
        summary,
      };

      acc[key] = composed;

      return acc;
    },
    {}
  );

  return portfolio;
}

/**
 * Orders Zones by supplied key
 * When two or more zones have the same value they're
 * sorted alphabetically by zone name
 *
 * @param rankDir 'best' | 'worst'
 * @param viewBy keyof Zone
 * @param data Zone[]
 */
export function orderZones(rankDir: RankDir, viewBy: ViewBy, data: Zone[]) {
  const dirNeg = rankDir === "best" ? "asc" : "desc";
  const sortedByName = orderBy(data, (d) => d.name, "asc");

  return orderBy(sortedByName, (d) => d[viewBy], dirNeg);
}

export function orderPortfolio(rankDir: RankDir, data: Portfolio) {
  // * Remove null values
  const pairs = Object.entries(data).filter((d) =>
    Boolean(d[1].summary.current.pctComfort)
  );
  if (rankDir === "best") {
    pairs.sort((a, b) =>
      a[1].summary.current.pctComfort >= b[1].summary.current.pctComfort
        ? -1
        : 1
    );
  }

  if (rankDir === "worst") {
    pairs.sort((a, b) =>
      a[1].summary.current.pctComfort <= b[1].summary.current.pctComfort
        ? -1
        : 1
    );
  }

  return pairs;
}
